import { lazy, Suspense } from "react";
import { Outlet, RouteObject } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { withAuth } from "@/hoc/withAuth";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { StudyTimeTrackingProvider } from "@/providers/StudyTimeTrackingProvider";
import { StudyAccessGuardProvider } from "@/providers/StudyAccessGuardProvider";
import RouteErrorFallback from "@/components/commons/fallback/RouteErrorFallback";

// Mobile-first layouts with safe-area support (keep static — needed immediately)
import { MobileLayoutWithAuth, MobileLayoutPublic, MobileLayoutPublicAuth, MockExamMobileLayoutWithAuth } from "@/components/layout/MobileLayout";
import { AppRouteNames } from "@/routes/constants";

// Login page imported directly (not lazy) — primary entry point for unauthenticated users
import { LoginPage } from "@/features/auth/pages/Login";
import { Dashboard } from "@/pages/dashboard/Dashboard";
import { StudyPage } from "@/features/study/pages/StudyPage";
import { StudySubjectPage } from "@/features/study/pages/StudySubjectPage";
import { SettingsPage } from "@/features/user-setting/pages/Settings";
import { VocabularyCategoryListPage } from "@/features/vocabulary/pages/VocabularyCategoryList";
import { VocabularyListPage } from "@/features/vocabulary/pages/VocabularyList";
import { MockExamListPage } from "@/features/mock-exam/pages/MockExamListPage";
import { MockExamPage } from "@/features/mock-exam/pages/MockExamPage";
import { SubjectContentPage } from "@/features/study/pages/SubjectContentPage";
import { StandaloneMobileLayout } from "@/components/layout/MobileLayout/StandaloneMobileLayout";

// Lazy-loaded page components
const MaintenancePage = lazy(() => import("@/features/maintenance/pages/Maintenance").then((m) => ({ default: m.MaintenancePage })));
const ConfirmEmail = lazy(() => import("@/pages/auth/ConfirmEmail"));
const PrivacyConsent = lazy(() => import("@/pages/auth/PrivacyConsent"));
const Logout = lazy(() => import("@/pages/auth/Logout"));
const Register = lazy(() => import("@/pages/auth/Register"));
const RegisterComplete = lazy(() => import("@/pages/auth/RegisterComplete"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const ResetPasswordComplete = lazy(() => import("@/pages/auth/ResetPasswordComplete"));
const PageNotFound = lazy(() => import("@/pages/PageNotFound"));
const ServerErrorPage = lazy(() => import("@/pages/ServerErrorPage"));
const UserSettingDetail = lazy(() => import("@/pages/user-setting/UserSettingDetail"));
const UserSettingEdit = lazy(() => import("@/pages/user-setting/UserSettingEdit"));
const UserSettingChangeEmail = lazy(() => import("@/pages/user-setting/UserSettingChangeEmail"));
const LicensePage = lazy(() => import("@/features/user-setting/pages/LicensePage").then((m) => ({ default: m.LicensePage })));
// const ContentProviderPage = lazy(() => import("@/features/user-setting/pages/ContentProviderPage").then((m) => ({ default: m.ContentProviderPage })));
const VocabularyPracticePage = lazy(() =>
  import("@/features/vocabulary/pages/VocabularyPractice").then((m) => ({ default: m.VocabularyPracticePage })),
);
const VocabularyQuizPage = lazy(() => import("@/features/vocabulary/pages/VocabularyQuiz").then((m) => ({ default: m.VocabularyQuizPage })));
const MockExamQuizPage = lazy(() => import("@/features/mock-exam/pages/MockExamQuizPage").then((m) => ({ default: m.MockExamQuizPage })));
const MockExamResultPage = lazy(() => import("@/features/mock-exam/pages/MockExamResultPage").then((m) => ({ default: m.MockExamResultPage })));
const DiagnosisQuizPage = lazy(() => import("@/features/study/pages/DiagnosisQuizPage").then((m) => ({ default: m.DiagnosisQuizPage })));
const WordPracticePage = lazy(() => import("@/features/study/pages/WordPracticePage").then((m) => ({ default: m.WordPracticePage })));
const WordQuizPage = lazy(() => import("@/features/study/pages/WordQuizPage").then((m) => ({ default: m.WordQuizPage })));
const LevelPracticePage = lazy(() => import("@/features/study/pages/LevelPracticePage").then((m) => ({ default: m.LevelPracticePage })));
const LevelQuizPage = lazy(() => import("@/features/study/pages/LevelQuizPage").then((m) => ({ default: m.LevelQuizPage })));
const TextExplanationViewerPage = lazy(() =>
  import("@/features/study/pages/TextExplanationViewerPage").then((m) => ({ default: m.TextExplanationViewerPage })),
);

/** Suspense wrapper for lazy-loaded route elements */
const S = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;

/** Suspense + study access guard wrapper */
const StudyGuardedSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingScreen />}>
    <StudyAccessGuardProvider>{children}</StudyAccessGuardProvider>
  </Suspense>
);

const DashboardWithAuth = withAuth(Dashboard);

// Full-screen pages (no layout) that still need auth protection
const DiagnosisQuizWithAuth = withAuth(DiagnosisQuizPage);
const WordPracticeWithAuth = withAuth(WordPracticePage);
const WordQuizWithAuth = withAuth(WordQuizPage);
const LevelPracticeWithAuth = withAuth(LevelPracticePage);
const LevelQuizWithAuth = withAuth(LevelQuizPage);
const TextExplanationWithAuth = withAuth(TextExplanationViewerPage);
const VocabularyPracticeWithAuth = withAuth(VocabularyPracticePage);
const VocabularyQuizWithAuth = withAuth(VocabularyQuizPage);
const MockExamQuizWithAuth = withAuth(MockExamQuizPage);
const MockExamResultWithAuth = withAuth(MockExamResultPage);

/** Root layout that provides study time tracking across all routes */
const TrackingLayout = () => (
  <StudyTimeTrackingProvider>
    <Outlet />
  </StudyTimeTrackingProvider>
);

export const routes: RouteObject[] = [
  {
    element: <TrackingLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        element: <MobileLayoutPublicAuth />,
        children: [
          {
            path: AppRoutes.login,
            element: <LoginPage />,
          },
          {
            path: AppRoutes.resetPassword,
            element: (
              <S>
                <ResetPassword />
              </S>
            ),
          },
          {
            path: AppRoutes.register,
            element: (
              <S>
                <Register />
              </S>
            ),
          },
          {
            path: AppRoutes.confirmEmail,
            element: (
              <S>
                <ConfirmEmail />
              </S>
            ),
          },
        ],
      },
      {
        element: <MobileLayoutPublic />,
        children: [
          {
            path: AppRoutes.resetPasswordComplete,
            element: (
              <S>
                <ResetPasswordComplete />
              </S>
            ),
          },
          {
            path: AppRoutes.registerComplete,
            element: (
              <S>
                <RegisterComplete />
              </S>
            ),
          },
          {
            path: AppRoutes.privacyConsent,
            element: (
              <S>
                <PrivacyConsent />
              </S>
            ),
          },
        ],
      },
      {
        element: <MobileLayoutWithAuth />,
        children: [
          {
            path: AppRoutes.userSettingDetail,
            element: (
              <S>
                <UserSettingDetail />
              </S>
            ),
            id: AppRouteNames.userSettingDetail.value,
          },
          {
            path: AppRoutes.userSettingEdit,
            element: (
              <S>
                <UserSettingEdit />
              </S>
            ),
            id: AppRouteNames.userSettingEdit.value,
          },
          {
            path: AppRoutes.userSettingChangeEmail,
            element: (
              <S>
                <UserSettingChangeEmail />
              </S>
            ),
            id: AppRouteNames.userSettingChangeEmail.value,
          },

          // Placeholder routes for bottom nav items

          {
            path: AppRoutes.study,
            element: <StudyPage />,
            id: AppRouteNames.study.value,
          },
          {
            path: AppRoutes.vocabularyCategoryList,
            element: <VocabularyCategoryListPage />,
            id: AppRouteNames.vocabularyCategoryList.value,
          },
          {
            path: AppRoutes.vocabularyDetail,
            element: <VocabularyListPage />,
            id: AppRouteNames.vocabularyDetail.value,
          },
          {
            path: AppRoutes.more,
            element: <SettingsPage />,
            id: AppRouteNames.more.value,
          },
          {
            path: AppRoutes.license,
            element: (
              <S>
                <LicensePage />
              </S>
            ),
            id: AppRouteNames.license.value,
          },
          // { path: AppRoutes.contentProvider, element: <S><ContentProviderPage /></S>, id: AppRouteNames.contentProvider.value },
          {
            path: AppRoutes.studySubject,
            element: (
              <StudyAccessGuardProvider>
                <StudySubjectPage />
              </StudyAccessGuardProvider>
            ),
            id: AppRouteNames.studySubject.value,
          },
          {
            path: AppRoutes.studyContents,
            element: (
              <StudyAccessGuardProvider>
                <SubjectContentPage />
              </StudyAccessGuardProvider>
            ),
            id: AppRouteNames.studyContents.value,
          },
        ],
      },
      {
        element: <MockExamMobileLayoutWithAuth />,
        children: [
          {
            path: AppRoutes.mockExam,
            element: <MockExamListPage />,
            id: AppRouteNames.mockExam.value,
          },
          {
            path: AppRoutes.mockExamDetail,
            element: <MockExamPage />,
            id: AppRouteNames.mockExamDetail.value,
          },
        ],
      },
      {
        element: <StandaloneMobileLayout />,
        children: [
          {
            path: AppRoutes.studyDiagnosis,
            element: (
              <StudyGuardedSuspense>
                <DiagnosisQuizWithAuth />
              </StudyGuardedSuspense>
            ),
            id: AppRouteNames.studyDiagnosis.value,
          },
          {
            path: AppRoutes.studyWordPractice,
            element: (
              <StudyGuardedSuspense>
                <WordPracticeWithAuth />
              </StudyGuardedSuspense>
            ),
            id: AppRouteNames.studyWordPractice.value,
          },
          {
            path: AppRoutes.studyWordQuiz,
            element: (
              <StudyGuardedSuspense>
                <WordQuizWithAuth />
              </StudyGuardedSuspense>
            ),
            id: AppRouteNames.studyWordQuiz.value,
          },
          {
            path: AppRoutes.studyLevelPractice,
            element: (
              <StudyGuardedSuspense>
                <LevelPracticeWithAuth />
              </StudyGuardedSuspense>
            ),
            id: AppRouteNames.studyLevelPractice.value,
          },
          {
            path: AppRoutes.studyLevelQuiz,
            element: (
              <StudyGuardedSuspense>
                <LevelQuizWithAuth />
              </StudyGuardedSuspense>
            ),
            id: AppRouteNames.studyLevelQuiz.value,
          },
          {
            path: AppRoutes.studyTextExplanation,
            element: (
              <StudyGuardedSuspense>
                <TextExplanationWithAuth />
              </StudyGuardedSuspense>
            ),
            id: AppRouteNames.studyTextExplanation.value,
          },
          {
            path: AppRoutes.vocabularyPractice,
            element: (
              <S>
                <VocabularyPracticeWithAuth />
              </S>
            ),
            id: AppRouteNames.vocabularyPractice.value,
          },
          {
            path: AppRoutes.vocabularyQuiz,
            element: (
              <S>
                <VocabularyQuizWithAuth />
              </S>
            ),
            id: AppRouteNames.vocabularyQuiz.value,
          },
          {
            path: AppRoutes.mockExamQuiz,
            element: (
              <S>
                <MockExamQuizWithAuth />
              </S>
            ),
            id: AppRouteNames.mockExamQuiz.value,
          },
          {
            path: AppRoutes.mockExamResult,
            element: (
              <S>
                <MockExamResultWithAuth />
              </S>
            ),
            id: AppRouteNames.mockExamResult.value,
          },
        ],
      },
      {
        path: AppRoutes.logout,
        element: (
          <S>
            <Logout />
          </S>
        ),
      },
      {
        path: AppRoutes.home,
        element: <DashboardWithAuth />,
        id: AppRouteNames.home.value,
      },
      {
        path: AppRoutes.serverErrorPage,
        element: (
          <S>
            <ServerErrorPage />
          </S>
        ),
      },
      {
        path: AppRoutes.maintenance,
        element: (
          <S>
            <MaintenancePage />
          </S>
        ),
      },
      {
        path: "*",
        element: (
          <S>
            <PageNotFound />
          </S>
        ),
      },
    ],
  },
];

const flattenRoutes = (routes: RouteObject[]): RouteObject[] => {
  const flatRoutes: RouteObject[] = [];

  const extractChildren = (route: RouteObject) => {
    if (route.children) {
      route.children.forEach((child) => {
        extractChildren(child); // Recursively extract children
      });
    } else {
      flatRoutes.push(route); // Push the route if there are no children
    }
  };

  routes.forEach((route) => extractChildren(route));

  return flatRoutes;
};

export const flattenedRoutes = flattenRoutes(routes);
